// API client + types for the draftlize-server backend.

import { pushRequestId } from './observability/request_ids'
import { crumb } from './observability/breadcrumbs'
import { isCapacitor } from './native/platform'
import { getMobileToken } from './native/mobile_token'
import type { Locale } from './i18n'

/**
 * PR-29: base URL for /api requests. Empty on the web build and the dev
 * live-reload app — same-origin, or the Vite proxy handles `/api`. A
 * bundled native build runs from `capacitor://localhost` with no proxy,
 * so `VITE_API_BASE` must point at the deployed API (e.g.
 * https://api.draftlize.com) — set it at build time for the iOS bundle.
 */
export const API_BASE: string = isCapacitor()
  ? import.meta.env.VITE_API_BASE || ''
  : ''

export interface Project {
  id: string;
  tenant_id: string;
  name: string;
  /** Stable external slug (PR-05) — the public address of the project for
   *  MCP / CLI clients (`draftlize link <slug>`). Never changes on rename. */
  slug: string;
  pack: string | null;
  created_at: string;
  updated_at: string;
  /** Number of unread threads in this project for the current user.
   *  Backed by a server-side join: COUNT of threads whose updated_at
   *  exceeds the user's last_read_at cursor (or has no cursor row).
   *  Used by the sidebar to dot off-screen project rows so a
   *  finished-while-you-were-elsewhere turn surfaces visually. */
  unread_thread_count?: number;
}

export interface Thread {
  id: string;
  tenant_id: string;
  project_id: string;
  title: string | null;
  archived: boolean;
  active_frame: string | null;
  created_at: string;
  updated_at: string;
  /** PR-56: per-thread model override. NULL = use server default
   *  (`DRAFTLIZE_MODEL`). Set via the model picker in the chat header.
   *  Vision sticky still wins on image threads — when the LLM has to
   *  read prior turns with attachments, the server forces a vision-
   *  capable model regardless of this field.
   *  PR-57: now stores either a tier id ("flash-1m") or a raw model
   *  id ("glm-5-turbo"). The server resolves either shape. */
  model: string | null;
  /** PR-57: graded reasoning effort ("high" | "max") when the active
   *  tier supports graded effort. NULL otherwise (and on tiers with
   *  on/off thinking like GLM). Ignored at request time when fast_mode
   *  is true. */
  effort: string | null;
  /** PR-57: UI fast-mode toggle. When true, the chat path forces
   *  thinking off regardless of tier and effort. */
  fast_mode: boolean;
  /** PR-56: surfaced for the picker UI to render a 👁 marker on
   *  threads where the chosen model would be overridden by vision
   *  sticky. Set on the first image upload, never reset. */
  had_attachment?: boolean;
  /** Server-stamped "most recent activity" — currently aliased to
   *  updated_at, which the message-persist path bumps on every turn.
   *  The client compares this against `last_read_at` to derive the
   *  unread state. Surfaced as a separate field so the comparison
   *  stays correct even if updated_at later picks up extra meanings. */
  last_message_at?: string;
  /** Per-user "I read up to here" cursor. Null when the current user
   *  has never opened this thread (treated as fully unread). Bumped to
   *  now() via POST /api/threads/{id}/read whenever the thread becomes
   *  the active one. */
  last_read_at?: string | null;
}

/** PR-57: spec for one user-visible tier. UI renders these in the
 *  picker; we expose `id` (stable, sent over the wire) and a display
 *  layer (name + description) but never the underlying provider model
 *  id. `thinking_kind` tells the UI how to render the Effort selector:
 *    - "none"   → no Effort row
 *    - "on_off" → Effort row hidden, only Fast mode toggle applies
 *    - "effort" → render Effort buttons using `effort_levels`
 */
export interface TierChoice {
  id: string;
  display_name: string;
  description: string;
  thinking_kind: 'none' | 'on_off' | 'effort';
  effort_levels: string[];
  default_effort: string | null;
}

/** PR-56 + PR-57: model picker payload returned by `GET /api/models`. */
export interface ModelChoices {
  /** PR-57: user-visible tiers, ordered as the picker should render. */
  tiers: TierChoice[];
  /** Default tier id (matches one of `tiers[].id`). */
  default_tier: string;
  /** Legacy raw-model list — kept for backward compat. New UI ignores. */
  choices: string[];
  /** Legacy default raw-model — kept for backward compat. */
  default: string;
}

export interface Card {
  tenant_id: string;
  project_id: string;
  card_id: string;
  kind: string;
  title: string | null;
  body: string;
  pack: string | null;
  stale: boolean;
  /** doc/11 PR-A3 — 4-state provenance:
   *  - `agent`: write_card direct (agent committed content)
   *  - `ai-suggested`: propose_card landing as kind=gap (awaiting user review)
   *  - `user`: card the user created themselves
   *  - `user-revised`: originally agent/ai-suggested, then user-patched
   *    (extra.prior_author stamps the previous state)
   *  Unknown forward-compat strings allowed; UI falls back to "BY AGENT". */
  author: 'agent' | 'user' | 'ai-suggested' | 'user-revised' | string;
  mtime: string;
  created_at: string;
  updated_at: string;
  /** Thread the card was first written in — null for cards created
   *  outside a chat turn (import, CLI write-back). Lets the mobile
   *  conversation surface a thread's own cards inline (doc/10 P1). */
  created_in_thread: string | null;
  extra: Record<string, unknown>;
  /** PR-41: implementation tracking. NULL for the bulk of cards (the
   *  default — most cards aren't shipped artifacts). Otherwise one of
   *  `proposed` / `building` / `built` / `drift_detected`. Set via the
   *  `setCardImplementationStatus` mutation below or the CLI/MCP
   *  `mark-status` surface. The server auto-promotes a `built` card to
   *  `drift_detected` whenever its title/body changes through the
   *  regular write paths. */
  implementation_status: ImplementationStatus | null;
  /** Lens MVP: soft-hide flag. Default false. When true, the card is
   *  filtered out of the default Time / Status lens view; surfaced only
   *  when the lens picker's "Show archived" toggle is on. Distinct from
   *  delete (hard removal). Use case: keep junk / one-off / test cards
   *  out of the main feed without losing their card_id. */
  archived: boolean;
}

/** PR-41: closed set the FE renders pills/tooltips for. Server validates
 *  the same allowlist; new states would need a PR on both sides. */
export type ImplementationStatus =
  | 'proposed'
  | 'building'
  | 'built'
  | 'drift_detected';

/**
 * One edge between two cards. Edges are extracted from wikilinks in card
 * bodies (and explicit user edits) and persisted in the `edges` table.
 *
 * `edge_type` matches the backend CHECK constraint on the column. The lens
 * view collapses these into 3 user-visible buckets:
 *   - incoming  ← target_card_id matches a card row, count up
 *   - outgoing  → source_card_id matches a card row, count up
 *   - conflict  ↮ both sides for `contradicts` (rendered specially)
 */
export interface Edge {
  tenant_id: string;
  project_id: string;
  source_card_id: string;
  target_card_id: string;
  edge_type:
    | 'supports'
    | 'depends-on'
    | 'derived-from'
    | 'connects'
    | 'contradicts';
}

/**
 * One message in the agent's `Session.messages` array.
 *
 * Wire format is `runtime::ConversationMessage` — claw-code's session shape.
 * Persisted as part of `threads.session` jsonb; one UPDATE per turn replaces
 * the old per-message rows. There are no per-message timestamps in this shape;
 * the frontend uses `Date.now()` when hydrating old threads (lossy but ok
 * for dev — UI just labels turns by HH:MM).
 */
export interface SessionMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  blocks: ContentBlock[];
  usage?: TokenUsage;
  /** Set when the LLM stopped because it hit `max_tokens` (provider
   *  `stop_reason` was `max_tokens`/`length`). Persisted on the assistant
   *  message so the bubble keeps its "cut-off" badge after a reload. */
  truncated?: boolean;
  /** Stable per-thread turn id (e.g. `t-42`) stamped at push time. Lets a
   *  card cite specific turns in `extra.context.turns` and lets the UI
   *  hover-highlight which messages a card was synthesized from. May be
   *  absent on legacy session blobs (the backend backfills positionally
   *  on load, so loaded messages always have one in practice). */
  id?: string;
}

export type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: string }
  | {
      type: 'tool_result';
      tool_use_id: string;
      tool_name: string;
      output: string;
      is_error: boolean;
    }
  // Reasoning / chain-of-thought emitted by thinking-enabled models.
  // The runtime persists this so reload renders the collapsed Thinking
  // section the user saw streaming live; absent on assistant turns where
  // the provider didn't emit a reasoning channel.
  | { type: 'thinking'; text: string };

export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

export interface SendMessageOutput {
  assistant_text: string;
  tool_calls: Array<{ id: string; name: string; input: string }>;
}

/**
 * Wire format for streaming a chat turn. Matches `draftlize_tools::TurnEvent`.
 * Multiple `assistant_start`/`assistant_end` pairs may interleave with
 * `tool_use_*` events as the runtime loops over tool calls; the UI tracks
 * which assistant message is "currently streaming" by the most recent
 * `assistant_start` that hasn't yet been terminated by `assistant_end`.
 */
export type TurnEvent =
  | { type: 'assistant_start' }
  /** Streaming reasoning text. Arrives before any `text_delta` when the
   *  model has thinking enabled (which our backend always opts into for
   *  GLM — same cost either way, GLM bills reasoning regardless). UI
   *  shows live in a dimmed bubble so the user can see what the agent is
   *  considering instead of staring at a static "thinking…" caret. */
  | { type: 'thinking_delta'; text: string }
  /** Reasoning block closed. Followed by `text_delta` (model is now
   *  answering) or `tool_use_start` (model decided to call a tool). UI
   *  uses this to lock the thinking bubble's content + collapse it. */
  | { type: 'thinking_end' }
  | { type: 'text_delta'; text: string }
  | { type: 'tool_use_start'; id: string; name: string }
  | { type: 'tool_use_input_delta'; id: string; partial: string }
  | { type: 'tool_use_end'; id: string; input: string }
  /** End of one LLM call. `truncated` is true when the provider stopped
   *  because of `max_tokens` / `length` — the agent message is cut off
   *  mid-thought and the UI should mark it. Field is omitted when false. */
  | { type: 'assistant_end'; truncated?: boolean }
  | { type: 'tool_result'; tool_use_id: string; tool_name: string; is_error: boolean }
  /** PR-55 / PR-58: per-turn token accounting, emitted once just before
   *  `turn_done`. Drives the chat header's context bar.
   *
   *  Three families of fields:
   *
   *  Provider-reported usage (`turn_*`, `session_total_*`):
   *    Billing-accurate input/output/cache split. `turn_*` is the
   *    cost of the just-finished turn (across any inner tool-loop
   *    iterations); `session_total_*` is cumulative for the thread.
   *
   *  Server-side input estimate (`pre_compact_messages`,
   *  `post_compact_messages`, `system_tokens`, `tools_tokens`):
   *    Describes what *actually filled the context window* this
   *    turn, broken down by component. `pre_compact_messages` is the
   *    persisted-full history's runtime-est size; `post_compact_messages`
   *    is what the LLM saw after auto-compaction (= pre when no
   *    compaction). `system_tokens` + `tools_tokens` use the server
   *    bytes/3 estimator on the rendered system prompt and serialized
   *    tools schema. `compact_triggered` + `compacted_message_count`
   *    surface the compaction event for "📦 N messages summarised"
   *    UI affordances.
   *
   *  Model capacity (`context_window`, `compact_threshold`):
   *    The active model's physical input limit, and the runtime-est
   *    line at which auto-compaction fires next time. Threshold
   *    scales with context_window — 200K tiers fire at ~100K, 1M
   *    tiers fire at ~580K.
   *
   *  `estimated_next_input` is deprecated (= `pre_compact_messages`),
   *  kept one release for older builds. */
  | {
      type: 'turn_usage'
      turn_input: number
      turn_output: number
      turn_cache_creation: number
      turn_cache_read: number
      session_total_input: number
      session_total_output: number
      session_total_cache_creation: number
      session_total_cache_read: number
      pre_compact_messages: number
      post_compact_messages: number
      system_tokens: number
      tools_tokens: number
      compact_triggered: boolean
      compacted_message_count: number
      context_window: number
      compact_threshold: number
      /** @deprecated equal to `pre_compact_messages`. */
      estimated_next_input: number
    }
  | { type: 'turn_done' }
  | { type: 'error'; message: string }
  /** Folded prefix of a turn's event log. Server emits this as the FIRST
   *  event of a stream when the client's requested `from_seq` falls
   *  behind the in-memory replay buffer (events have aged out). Carries
   *  the reconstructed state of every event with seq ≤ `seq_through`.
   *  Client must replace this turn's in-flight bubbles with the
   *  snapshot's content, then continue applying subsequent delta events
   *  from `seq_through + 1` onward. Old builds that don't recognise this
   *  variant silently ignore it (and remain exposed to the silent-gap
   *  bug — refresh once the new bundle ships). */
  | {
      type: 'compacted_snapshot'
      seq_through: number
      completed_messages: CompactedMessage[]
      in_progress: CompactedMessage | null
    };

/** One assistant message block reconstructed from folded deltas — mirrors
 *  the shape the live reducer would assemble bubble-by-bubble. */
export interface CompactedMessage {
  text: string
  thinking: string
  thinking_ended: boolean
  truncated: boolean
  tool_calls: CompactedToolCall[]
}

export interface CompactedToolCall {
  id: string
  name: string
  /** Accumulated `tool_use_input_delta.partial` while `tool_use_end` is
   *  still pending. Empty once `input` is set. */
  input_partial: string
  /** Set when `tool_use_end` has been folded. Presence is the signal
   *  that the tool call is finalised on the wire. */
  input: string | null
}

/** PR-55 / PR-58: token-usage snapshot for a thread. Fetched on thread
 *  open via `getThreadUsage`; subsequently refreshed by the SSE
 *  `turn_usage` event the server emits once at the end of each turn.
 *  Same shape as that event minus the per-turn delta (no "this turn"
 *  before any turn has run on this open). See the `turn_usage` doc for
 *  field semantics. */
export interface ThreadUsage {
  session_total_input: number;
  session_total_output: number;
  session_total_cache_creation: number;
  session_total_cache_read: number;
  pre_compact_messages: number;
  post_compact_messages: number;
  system_tokens: number;
  tools_tokens: number;
  compact_triggered: boolean;
  compacted_message_count: number;
  context_window: number;
  compact_threshold: number;
  /** @deprecated equal to `pre_compact_messages`. */
  estimated_next_input: number;
}

export type WireEvent =
  | {
      type: 'card_written'
      card_id: string
      kind: string
      created: boolean
      /** Wikilink targets in the body that don't resolve to any card.
       *  Empty when everything's well-formed. UI shows a toast and asks
       *  DCard to render the body's `[[…]]` references in red strike. */
      broken_wikilinks: string[]
    }
  | { type: 'card_updated'; card_id: string; kind: string }
  | { type: 'card_deleted'; card_id: string; broken_links: string[] }
  /** A decision card was re-asserted; these dependents transitioned to
   *  `stale = true` via transitive →depends-on. The UI re-fetches the card
   *  list and shows a toast naming the triggering decision. */
  | { type: 'cards_marked_stale'; decision_id: string; card_ids: string[] }
  /** Saved documents whose snapshot of one or more source cards has drifted
   *  (or whose source card was deleted). The UI puts a badge on the
   *  Documents button so the user notices without opening the modal first. */
  | { type: 'documents_stale'; documents: Array<{ id: string; title: string }> }
  /** Coarse-grained "frames in this project changed" — frontend refetches
   *  the frame list. Fires for create / rename / delete / placement
   *  override / recompute. No payload because the recovery path is always
   *  refetch-and-diff. */
  | { type: 'frames_changed' }
  /** Coarse-grained "threads in this project changed" — frontend refetches
   *  the thread list. Fires for create / archive / rename / delete. */
  | { type: 'threads_changed' }
  /** Coarse-grained "project metadata changed" — frontend refetches the
   *  project list. Fires for rename / pack swap / MCP toggle. */
  | { type: 'project_changed' }
  /** A new chat turn just started on this thread. Devices currently
   *  viewing the same thread should call `attachTurn(thread_id, turn_id)`
   *  to follow the turn in real time. Devices on a different thread
   *  can ignore this event. */
  | { type: 'thread_turn_started'; thread_id: string; turn_id: string }
  /** The turn finished. `success=false` means the runtime errored out
   *  rather than running to completion. Devices that weren't attached
   *  should refetch the thread's session for the canonical post-turn
   *  state. */
  | { type: 'thread_turn_ended'; thread_id: string; turn_id: string; success: boolean };

/** Server response to POST /api/threads/{tid}/messages — the turn is
 *  running on the server; clients call `attachTurn(thread_id, turn_id)`
 *  to subscribe to its event stream. The same `turn_id` is broadcast
 *  to other devices via the project SSE `thread_turn_started` event. */
export interface StartTurnResponse {
  turn_id: string;
  thread_id: string;
  /** ISO-8601 timestamp of when the turn was claimed in the hub. */
  started_at: string;
}

/** Returned by GET /api/threads/{tid}/active_turn. `null` when the
 *  thread has no turn in flight (or has just finished and dropped from
 *  the hub's grace window). */
export interface ActiveTurn {
  turn_id: string;
  thread_id: string;
  started_at: string;
}

export interface CardPatch {
  kind?: string;
  title?: string;
  body?: string;
  pack?: string;
}

/**
 * One revision of a card. Mirrors `card_history` table rows. Snapshot
 * fields (kind/title/body/pack/extra) are populated on every revision
 * including op='delete' (the delete row carries the prior state so the
 * Deleted view doesn't need a join).
 *
 * `source_message_snippet` is a 200-char excerpt of the user message that
 * triggered the write — captured at write time so it survives a future
 * session compaction. None for HTTP / fs_sync paths.
 */
export interface CardHistoryRevision {
  tenant_id: string;
  project_id: string;
  card_id: string;
  revision: number;
  /** What kind of mutation produced this row. */
  op: 'write' | 'patch' | 'promote' | 'fs_sync' | 'delete';
  author: 'agent' | 'user' | 'fs';
  kind: string | null;
  title: string | null;
  body: string | null;
  pack: string | null;
  extra: Record<string, unknown> | null;
  body_hash: string | null;
  /** Only meaningful for op='delete'. e.g. "rejected: foo", "resolved by c-3". */
  delete_reason: string | null;
  source_thread_id: string | null;
  source_message_index: number | null;
  source_message_role: string | null;
  source_message_snippet: string | null;
  created_at: string;
}

export interface DeleteCardOutput {
  card_id: string;
  broken_links: string[];
}

export interface GenerateOutput {
  markdown: string;
  template: string;
  card_count: number;
}

/** Persisted long-form artifact — created by `saveDocument` after a
 *  generate completes, fetched by `listDocuments` / `getDocument`.
 *  `source_snapshot` is what powers the "regenerate?" stale check
 *  (compare each entry's mtime against the live card's current mtime). */
export interface DocumentRecord {
  id: string;
  tenant_id: string;
  project_id: string;
  title: string;
  template: string;
  markdown: string;
  source_snapshot: Array<{ card_id: string; mtime: string }>;
  generated_at: string;
  created_at: string;
  updated_at: string;
}

export type DocTemplate = 'design-doc' | 'pitch-deck' | 'blog-post' | 'outline';

export interface DocTemplateSpec {
  id: DocTemplate;
  label: string;
  blurb: string;
}

export const DOC_TEMPLATES: DocTemplateSpec[] = [
  { id: 'design-doc', label: 'Design doc',  blurb: 'Game / product spec — vision, mechanics, constraints, risks.' },
  { id: 'pitch-deck', label: 'Pitch',       blurb: 'Founder narrative — problem, insight, what we are building, why now.' },
  { id: 'blog-post',  label: 'Blog post',   blurb: 'A builder writeup — what we tried, what got in the way, what we learned.' },
  { id: 'outline',    label: 'Outline',     blurb: 'Tight bullet structure with `[c-N]` citations. No prose.' },
];

export interface PackDef {
  id: string;
  name: string;
  description: string;
  recommended_kinds: string[];
  /** Frame template ids the pack recommends. Surfaced in AddFrameModal as
   *  a "Recommended by {pack}" badge and sorted first. Empty = no opinion. */
  recommended_frame_templates: string[];
  system_prompt_fragment: string;
}

/** One audit-log row from the draftlize-mcp server. Newest first in the
 *  status response. `card_id` is null for tool calls that aren't
 *  card-scoped (list_cards / search_cards / list_resources). */
export interface McpAccessEvent {
  id: number;
  tenant_id: string;
  project_id: string;
  card_id: string | null;
  /** Logical operation: read_card / list_cards / search_cards / read_resource. */
  tool_name: string;
  /** Best-effort client identifier from the MCP `initialize` handshake.
   *  Null when the client didn't announce itself. */
  client_name: string | null;
  /** RFC3339 UTC timestamp. */
  ts: string;
}

export interface McpStatus {
  project_id: string;
  /** Whether the draftlize-mcp server exposes this project right now.
   *  Toggled in Settings → Integrations. */
  enabled: boolean;
  /** Number of access events in the trailing 24h window. */
  recent_count_24h: number;
  /** RFC3339 timestamp of the latest event, or null if none in 24h. */
  last_event_at: string | null;
  /** Up to 20 most recent events, newest first. */
  recent_events: McpAccessEvent[];
  /** Distinct card ids read in the last 24h. CardsPanel uses this to show
   *  a ⚡ "recently consumed by external" badge on matching rows. */
  recently_read_card_ids: string[];
}

export interface RefEntry {
  name: string;
  size_bytes: number;
  /** RFC3339 modified timestamp. */
  modified: string;
}

// ── Frames ──────────────────────────────────────────────────────────

/**
 * A node in a frame's topology — one MECE bucket. `path` is slash-separated;
 * descendants prefix their parent's path. `mece_required` nodes are surfaced
 * as gaps when empty.
 */
export interface FrameNode {
  path: string;
  label: string;
  description?: string;
  mece_required?: boolean;
  rule?: string;
  /** Pack-prebuilt prompt questions surfaced when the user clicks a frame
   *  MECE gap chip — rendered in GapProbePopover, each clickable to send the
   *  question to the agent as a user message. */
  probes?: string[];
}

export type FrameMode = 'rule' | 'llm';

export interface FrameDef {
  id: string;
  name: string;
  description: string;
  mode: FrameMode;
  priority: number;
  topology: FrameNode[];
}

export interface PlacementInfo {
  node_path: string;
  override_by_user: boolean;
}

/** Sentinel node paths the categorizer may emit. Render specially in UI. */
export const PATH_UNASSIGNED = '__unassigned__';
export const PATH_EXCLUDED = '__excluded__';

export interface FrameView {
  frame: FrameDef;
  placements: Record<string, PlacementInfo>;
  /** Paths of mece_required nodes that have zero cards. */
  mece_gaps: string[];
  /** True if the response triggered a recompute (vs cache hit). */
  recomputed: boolean;
}

/**
 * Wire format for streaming `suggestFrame`. Matches `SuggestWireEvent` in
 * `routes.rs`. `tick` events drive the progress bar; the terminal frame is
 * either `done` (proposal ready) or `error`.
 */
export type SuggestEvent =
  | { type: 'started'; card_count: number }
  | { type: 'tick'; bytes: number }
  | { type: 'done'; proposal: FrameDef; card_count: number }
  | { type: 'error'; message: string };

/**
 * Wire frames for the streaming `generate` endpoint. UI flow:
 *   - `started` once with card_count → render modal in "preparing" state
 *   - `delta` as text streams in → append to the live markdown buffer
 *   - `done` once with the full markdown → finalise (caller can stop showing
 *     the streaming spinner; copy/download buttons become safe to use)
 *   - `error` once if generation fails (may follow earlier deltas)
 */
export type GenerateEvent =
  | { type: 'started'; card_count: number }
  | { type: 'delta'; text: string }
  | { type: 'done'; markdown: string; template: string; card_count: number }
  | { type: 'error'; message: string };

/** A card the distiller proposes from a thread — content the user
 *  effectively already said. Nothing's persisted until the user confirms;
 *  on confirm, lands via `write_card`. */
export interface CardProposal {
  kind: string;
  title: string;
  body: string;
  /** One-line "what conversation moment is this card capturing"; shown in
   *  the review modal so the user knows why each card was proposed. */
  rationale?: string;
}

/** A gap the distiller proposes from a thread — an open item the user
 *  needs to resolve (interpretation, missing fact, undecided question,
 *  conflict). On confirm, lands via `propose_card` and surfaces in the
 *  Gap Ledger for Approve / Discuss / Reject. */
export interface GapProposal {
  /** Kind this gap would become on Approve (statement / decision / etc.). */
  proposed_kind: string;
  title: string;
  body: string;
  /** User-readable explanation (≥10 chars) of why this is a gap rather
   *  than a confident card. Persisted on the gap card on confirm. */
  ambiguity_reason: string;
  /** Priority bucket. Required in audit/decompose modes; absent in rescue.
   *  Renders as a chip in OPEN and drives sort/group order. */
  priority?: 'p0' | 'p1' | 'p2' | 'p3';
  /** Card ids this gap blocks. Drives `dismissed_gaps` targeting on Reject. */
  blocks?: string[];
  /** One-line "what conversation moment surfaced this gap"; shown in the
   *  review modal alongside the gap. */
  rationale?: string;
}

/** Distill mode. Picks which system prompt drives the extraction.
 * - `rescue`   — current default. Capture missed crystallisations from a
 *                conversation. Light bias toward GAP when uncertain.
 * - `decompose` — the agent produced a structured artifact (listicle,
 *                hierarchy, audit). Explode it into atomic items; strong
 *                bias toward GAP for "X missing" claims.
 * - `audit`    — project-wide gap inventory against an audit lens (inferred
 *                from the conversation). Output is ~95% gaps with priority. */
export type DistillMode = 'rescue' | 'decompose' | 'audit';

export type DistillEvent =
  | { type: 'started' }
  | { type: 'tick'; items: number }
  | { type: 'done'; cards: CardProposal[]; gaps: GapProposal[] }
  | { type: 'error'; message: string };

export interface DistillOutput {
  cards: CardProposal[];
  gaps: GapProposal[];
}

/** PR-04 / PR-39: one row in the user's API-token list. The raw secret
 *  is NEVER returned by GET /api/tokens — only by POST /api/tokens at
 *  creation time, exactly once. The UI shows `prefix` (`dft_aB3kZ9pQ`)
 *  so users can match a row to the token they pasted into a CLI / MCP
 *  client without holding the full secret. */
export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  scopes: string[] | unknown;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

/** PR-04 / PR-39: response to POST /api/tokens. `raw_token` is the only
 *  surface for the secret — once the modal closes, it's gone forever
 *  (only the sha256 sits in PG). The UI must show + copy it before the
 *  modal dismisses. */
export interface CreateApiTokenResponse extends ApiToken {
  raw_token: string;
}

/**
 * PR-10: name of the custom event the api wrapper dispatches whenever any
 * /api/* response comes back 401. App.tsx listens for it and flips
 * authState to 'out' — which renders <LoginPage /> in place of the app
 * shell. Keeps the auth gate centralised here so every fetch flows through
 * one chokepoint (rather than each handler having to re-check status).
 */
export const UNAUTHORIZED_EVENT = 'draftlize:unauthorized';

// 计费重构（2026-06-28）：paywall 事件。LLM 调用返回 402 +
// kind=insufficient_balance（预付费钱包余额不足）时 dispatch。App.tsx
// 监听并弹 PaywallModal（充值引导）。与 UNAUTHORIZED_EVENT 同样的解耦 ——
// 把 fetch helper 与 React state 隔开。
export const PAYWALL_EVENT = 'draftlize:paywall';

/** Detail payload carried on the PAYWALL_EVENT CustomEvent (and stashed
 *  on the thrown PaywallError). 计费重构后只有「余额不足」一种 paywall。 */
export interface PaywallInfo {
  kind: 'insufficient_balance';
  /** 人类可读的说明（后端 402 body 的 detail）。 */
  detail?: string;
}

export interface PaywallError extends Error {
  paywall: PaywallInfo;
}

/** 计费重构（2026-06-28）：钱包余额快照（/api/wallet 与 /api/me 的 wallet
 *  字段）。`balance_cents` 为美分（÷100 得美元），可为负（透支语义）。
 *  `low_balance` 标记余额低于 $1，前端据此提示充值。 */
export interface WalletStatus {
  balance_cents: number;
  low_balance: boolean;
}

/** 钱包流水一行（/api/wallet/transactions）。金额以 tmc（万分之一分）返回，
 *  ÷1_000_000 得美元，可精确显示 sub-cent 的单次消费。 */
export interface WalletTransaction {
  id: string;
  amount_tmc: number;
  balance_after_tmc: number;
  /** signup_bonus | topup | spend | refund | adjust */
  kind: string;
  created_at: string;
}

export function isPaywallError(e: unknown): e is PaywallError {
  return (
    e instanceof Error &&
    'paywall' in e &&
    !!(e as PaywallError).paywall
  );
}

/**
 * PR-10: shared fetch wrapper that ALWAYS sends cookies (so the
 * `draftlize_session` cookie rides along on every request) and converts
 * any 401 into a `UNAUTHORIZED_EVENT` dispatch + thrown UNAUTHORIZED
 * error. Direct `fetch(...)` calls in this file are migrated to this
 * helper so the gate covers everything — JSON, multipart uploads,
 * SSE streams, and binary downloads alike.
 */
export const apiFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> => {
  // PR-31: breadcrumb the request before it leaves so a network
  // failure mid-flight (which throws and never reaches the post-await
  // code below) still leaves a trail.
  const t0 = Date.now();
  let path = '';
  try {
    const urlStr = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request).url;
    path = new URL(urlStr, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    path = String(input);
  }
  const method = (init.method ?? 'GET').toUpperCase();
  crumb('fetch', { phase: 'start', method, path });
  // PR-29: native app — prefix `/api` paths with the deployed API base
  // (the bundled WebView has no dev proxy) and carry the session in an
  // Authorization header (a WebView can't send the cookie cross-origin).
  const target =
    typeof input === 'string' && input.startsWith('/')
      ? API_BASE + input
      : input;
  const headers = new Headers(init.headers);
  const mobileToken = getMobileToken();
  if (mobileToken) headers.set('Authorization', `Bearer ${mobileToken}`);
  // PR-29: the native app authenticates with a bearer token, not a cookie.
  // A credentialed cross-origin request is rejected by the browser when the
  // server replies Access-Control-Allow-Origin: * — so omit credentials on
  // the native app; the web build (same-origin) keeps the session cookie.
  const credentials = isCapacitor() ? 'omit' : 'include';
  let res: Response;
  try {
    res = await fetch(target, { credentials, ...init, headers });
  } catch (e) {
    // Network-level throw (DNS, TLS, abort, extension hook). Crumb
    // the failure shape so reportClientError's recent_crumbs ties
    // the throw to the originating fetch.
    crumb('fetch', {
      phase: 'failed',
      method,
      path,
      duration_ms: Date.now() - t0,
      error: (e as Error)?.message ?? String(e),
    });
    throw e;
  }
  // PR-31: stash the response's `x-request-id` (server-stamped in
  // request_id.rs) into a small FIFO ring. `reportClientError` reads
  // the ring when a try/catch fires so the error row can point back
  // to the matching backend log span.
  // Best-effort — non-Draftlize fetches via this helper still benefit;
  // missing-header responses just get skipped.
  const rid = res.headers.get('x-request-id');
  if (rid) {
    pushRequestId({ path, id: rid, status: res.status });
  }
  crumb('fetch', {
    phase: 'done',
    method,
    path,
    status: res.status,
    duration_ms: Date.now() - t0,
    request_id: rid,
  });
  if (res.status === 401) {
    // Notify App.tsx so it can short-circuit to the login page.
    // Only fire in browser contexts (SSR-safe even though we're SPA-only today).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    throw new Error('UNAUTHORIZED');
  }
  return res;
};

const j = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
};

export interface MeResponse {
  user_id: string;
  /** PR-10: tenant the authed user belongs to. Single-tenant per user in v0. */
  tenant_id: string;
  /** PR-10: human-readable tenant name (e.g. "dev" for the bootstrap
   *  tenant; `<github_login>` for OAuth-provisioned ones). Reserved for
   *  future multi-tenant tenant-switcher; not surfaced in the UI yet. */
  tenant_name: string;
  email: string | null;
  display_name: string | null;
  /** PR-10: GitHub login snapshot (display only). Powers the sidebar
   *  "@cooldk · sign out" affordance. Null for users not linked to
   *  GitHub (only the dev bootstrap user today). */
  github_login: string | null;
  last_project_id: string | null;
  /** PR-12: whether the user's email has been verified. All current auth
   *  paths (GitHub OAuth, Google OAuth, magic-link) produce TRUE on
   *  first login, so the UI has nothing to react to today. Threaded for
   *  future paths (password signup, admin verification revoke) where
   *  the UI will render a "please verify your email" banner and gate
   *  LLM-spending features behind it. */
  email_verified: boolean;
  /** True when the server's `require_admin` would let this user through
   *  (currently: dev tenant's first user). Drives the sidebar admin
   *  links — the server check is still the real boundary. */
  is_admin: boolean;
  /** PR-15 (i18n): the user's saved locale preference, or null when
   *  unset — the FE then falls back to browser detection. Supported set
   *  lives in `i18n/index.ts` (Locale). */
  locale: Locale | null;
  /** (project_id → last_thread_id | null). Boot-time snapshot the FE
   *  uses to land on the exact (project, thread) the user was on. */
  project_views: Record<string, string | null>;
  /** 计费重构（2026-06-28）：预付费钱包余额快照，驱动 Settings → Billing、
   *  余额常驻显示与充值提示。 */
  wallet: WalletStatus;
}

/** Subscription state mirror — kept in sync with `billing.rs`'s
 *  SubscriptionStatus struct. */
export interface SubscriptionStatus {
  /** "pro" | "power" | "max". */
  tier: string;
  /** Stripe lifecycle: "trialing" | "active" | "past_due" | "canceled"
   *  | "incomplete" | "incomplete_expired" | "unpaid" | "paused". */
  status: string;
  /** ISO 8601 — trial end for legacy trials started before the no-trial
   *  switch (2026-06-28). Null for new subs (charged immediately) and
   *  for subscriptions that never trialed. */
  trial_ends_at: string | null;
  /** ISO 8601 — end of the currently-paid period; renewal happens here. */
  current_period_ends_at: string | null;
  /** True after user clicks "Cancel subscription" — they still have
   *  paid access until `current_period_ends_at`. */
  cancel_at_period_end: boolean;
  /** Computed server-side: `status IN ('trialing','active')`. Single
   *  truth-value the FE uses to decide whether to gate vs allow. */
  is_entitled: boolean;
}

/**
 * Set of turn_ids THIS client started (via `startTurn`). Lets observer-
 * style handlers tell "my own turn just ended" apart from "another
 * device on the same project just finished a turn".
 *
 * Why this matters: when a turn ends we'd normally refetch the session
 * blob to land observer devices on the canonical post-turn messages.
 * But the originating device has been accumulating thinking_delta
 * bubbles locally, and the server explicitly does NOT persist reasoning
 * (see `TurnEvent::ThinkingDelta` comment in runtime). A refetch on
 * the originator would replace its rich streamed view with the
 * thinking-less server snapshot. So: turn ids we started ourselves
 * skip the refetch; turn ids from other devices still refetch.
 *
 * Entries auto-expire after `MY_TURN_TTL_MS` to bound the set's
 * memory — well past any realistic late-arriving thread_turn_ended.
 */
const myStartedTurnIds = new Set<string>();
const MY_TURN_TTL_MS = 60_000;

/** True iff `startTurn` returned this turn_id from THIS browser tab
 *  within the last MY_TURN_TTL_MS. Used by project SSE handlers. */
export function isMyTurn(turnId: string): boolean {
  return myStartedTurnIds.has(turnId);
}

export const api = {
  health: () => apiFetch('/api/health').then(r => r.text()),

  listProjects: () => apiFetch('/api/projects').then(j<Project[]>),

  /** `pack` defaults to blank server-side; pass an id to opt into domain
   *  guidance (e.g. 'creation' / 'analysis'). */
  createProject: (name: string, pack?: string) =>
    apiFetch('/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, pack }),
    }).then(j<Project>),

  /** SEO-template funnel: create a project pre-seeded with the card
   *  structure a template landing page promised (prd / decision-log /
   *  competitive-analysis / gtm / product-brief). `lang` picks the seed
   *  content language ('en' | 'zh'). */
  createProjectFromTemplate: (template: string, lang: string) =>
    apiFetch('/api/projects/from-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, lang }),
    }).then(j<Project>),

  renameProject: (projectId: string, name: string) =>
    apiFetch(`/api/projects/${projectId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(j<Project>),

  /**
   * Mutate any subset of `name` / `pack` on a project. `pack: ""` clears
   * back to "blank"; omit a field to leave it untouched.
   */
  patchProject: (
    projectId: string,
    patch: { name?: string; pack?: string },
  ) =>
    apiFetch(`/api/projects/${projectId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(j<Project>),

  /** Hard-delete a project. Cascades through cards / threads / history /
   *  edges / frames / documents on the server. Local fs files are NOT
   *  removed (acceptable for v1; Settings UI warns about this). */
  deleteProject: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}`, { method: 'DELETE' }).then(
      j<{ project_id: string; deleted: true }>,
    ),

  listPacks: () => apiFetch('/api/packs').then(j<PackDef[]>),

  /** MCP integration status snapshot for one project. Drives Settings →
   *  Integrations and the sidebar connection chip. `recent_count_24h` and
   *  `last_event_at` are the cheap parts (chip uses them); `recent_events`
   *  / `recently_read_card_ids` power the audit list and per-card badges. */
  getMcpStatus: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/mcp/status`).then(j<McpStatus>),

  /** Toggle MCP exposure for one project. Returns the new value. */
  setMcpEnabled: (projectId: string, enabled: boolean) =>
    apiFetch(`/api/projects/${projectId}/mcp`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }).then(j<{ project_id: string; enabled: boolean }>),

  /**
   * Inverse of `exportProject`. POSTs a multipart upload of an export zip
   * and returns the freshly-created `Project`. The server validates the
   * archive (manifest.json, export_kind, format_version) and rebuilds the
   * project transactionally; on failure the response is a 4xx/5xx with
   * `{ error: "..." }`. `nameOverride` lets the user pick a display name
   * different from the source's; pass undefined / empty to get the default
   * `"<original> (imported)"`.
   */
  importProject: async (file: File, nameOverride?: string): Promise<Project> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (nameOverride && nameOverride.trim()) {
      fd.append('name', nameOverride.trim());
    }
    const res = await apiFetch('/api/projects/import', { method: 'POST', body: fd });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // The server emits `{"error": "..."}`. Surface that string raw if we
      // can parse it, fall back to status text otherwise.
      let msg = text;
      try {
        const j = JSON.parse(text) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        /* not JSON — keep the raw body */
      }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.json() as Promise<Project>;
  },

  /**
   * Download every live card, deleted card, full revision history, and
   * thread transcript for a project as a single ZIP. The server streams
   * `application/zip` with a `Content-Disposition` filename; we hand the
   * blob to the browser via a synthetic anchor click.
   *
   * Throws if the response isn't 2xx so the caller can surface an error
   * toast instead of silently saving an HTML 500 page as a `.zip`.
   */
  exportProject: async (projectId: string): Promise<void> => {
    const res = await apiFetch(`/api/projects/${projectId}/export`);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`export failed (${res.status}): ${text}`);
    }
    // Pull the server's filename from Content-Disposition. Falls back to
    // a sensible default if the header is missing or malformed.
    const cd = res.headers.get('content-disposition') ?? '';
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] ?? `draftlize-export-${Date.now()}.zip`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a short delay; revoking immediately can race the
    // download in some browsers (looking at you, Safari).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  // ── Refs ──────────────────────────────────────────────────────────
  listRefs: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/refs`).then(j<RefEntry[]>),

  /** Upload a single file as a ref. Filename is taken from the File object. */
  uploadRef: async (projectId: string, file: File): Promise<RefEntry> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await apiFetch(`/api/projects/${projectId}/refs`, {
      method: 'POST',
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`upload failed (${res.status}): ${text}`);
    }
    return res.json() as Promise<RefEntry>;
  },

  readRef: (projectId: string, name: string) =>
    apiFetch(`/api/projects/${projectId}/refs/${encodeURIComponent(name)}`).then(
      (r) => {
        if (!r.ok) throw new Error(`read ref failed: ${r.status}`);
        return r.text();
      },
    ),

  deleteRef: (projectId: string, name: string) =>
    apiFetch(`/api/projects/${projectId}/refs/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }).then(j<{ name: string; deleted: true }>),

  /**
   * Boot-time fetch of the current user (PR-10). Returns 401 when no
   * valid session cookie — App.tsx flips to the login page on that.
   *
   * `project_views` is a {projectId → threadId | null} map covering every
   * project the user has touched. A null entry means the user explicitly
   * viewed that project with no active thread; an absent entry means they
   * never opened it. `last_project_id` and `project_views[id]` can both be
   * null even on success — that's the "first run" state.
   */
  me: () => apiFetch('/api/me').then(j<MeResponse>),

  /**
   * PR-10: log out — server-side revokes the session row + clears the
   * `draftlize_session` cookie via Set-Cookie. The 204 is the success
   * signal. Errors are swallowed: even if the network is dead, the FE
   * still wants to flip to the login page (App.tsx does that
   * unconditionally after this resolves).
   */
  logout: async (): Promise<void> => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // already-out, transient network — caller flips to LoginPage anyway.
    }
  },

  /** doc/07 PR-44: GDPR hard-delete of the caller's account + ALL data
   *  (tenant cascade + user-keyed tables + S3 refs). Irreversible. The
   *  server revokes the session as part of this, so the next /api/me
   *  401s → login page. Caller should hard-reload after success. */
  deleteAccount: async (): Promise<{ deleted: boolean }> => {
    const res = await apiFetch('/api/me', { method: 'DELETE' });
    return res.json() as Promise<{ deleted: boolean }>;
  },

  /**
   * PR-13: pre-login probe for the hCaptcha widget. Returns whether the
   * server has hCaptcha configured and the public site key the FE should
   * render the widget with. `enabled: false` means "skip the widget
   * entirely" (typical fresh-dev-machine state where neither half of
   * the config is set). No auth required; this is read before login.
   */
  captchaConfig: async (): Promise<{ enabled: boolean; site_key: string | null }> => {
    const res = await apiFetch('/api/auth/captcha-config');
    if (!res.ok) throw new Error(`captchaConfig ${res.status}`);
    return res.json();
  },

  /**
   * PR-09: request a magic-link sign-in email. Server always returns
   * 200 `{ sent: true }` regardless of whether the email is registered —
   * we never want an outsider to be able to enumerate our user table by
   * polling this endpoint. The only failure modes that surface here are
   * "invalid email" (4xx), "captcha verification failed" (4xx, PR-13),
   * and "Resend not configured" (500).
   *
   * `captchaToken` is the token produced by the hCaptcha widget on the
   * login page; pass null when the server reports captcha disabled (the
   * server then accepts the request without verifying).
   */
  requestMagicLink: async (
    email: string,
    captchaToken: string | null,
    inviteCode?: string | null,
  ): Promise<void> => {
    const body: { email: string; captcha_token?: string; invite_code?: string } = { email };
    if (captchaToken) body.captcha_token = captchaToken;
    if (inviteCode) body.invite_code = inviteCode;
    const res = await apiFetch('/api/auth/email/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // doc/12 §3.1: invite-gate rejections carry a machine-readable
      // kind the login page maps to specific copy.
      const text = await res.text().catch(() => '');
      let kind: string | undefined;
      try {
        kind = (JSON.parse(text) as { kind?: string }).kind;
      } catch {
        // non-JSON body — fall through to the generic error
      }
      const err = new Error(`requestMagicLink ${res.status}: ${text}`) as Error & {
        inviteKind?: string;
      };
      if (kind === 'invite_required' || kind === 'invite_invalid') err.inviteKind = kind;
      throw err;
    }
  },

  /**
   * doc/12 §3.4.2: redeem the 6-digit code from the magic-link email.
   * Success sets the session cookie; caller reloads. The WeChat-webview
   * path where clicking the email link is unreliable.
   */
  verifyEmailCode: async (email: string, code: string): Promise<void> => {
    const res = await apiFetch('/api/auth/email/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!res.ok) throw new Error(`verifyEmailCode ${res.status}`);
  },

  /**
   * doc/12 §3.1: pre-login probe — should the login page render the
   * invite-code field? Mirrors captchaConfig's failure posture (treat
   * unreachable as gate-off so the form still works).
   */
  inviteConfig: async (): Promise<{ gate: boolean }> => {
    const res = await apiFetch('/api/auth/invite-config');
    if (!res.ok) throw new Error(`inviteConfig ${res.status}`);
    return res.json();
  },

  /** doc/12 §3.1.2: code-less visitors leave an email. Idempotent. */
  joinWaitlist: async (email: string, locale?: string): Promise<void> => {
    const res = await apiFetch('/api/waitlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, locale }),
    });
    if (!res.ok) throw new Error(`joinWaitlist ${res.status}`);
  },

  // ── Shares (doc/12 §3.2.1) ───────────────────────────────────────

  /** Create a read-only share link. kind='document' snapshots the
   *  markdown; kind='graph' renders the project's cards live. */
  createShare: (
    projectId: string,
    kind: 'document' | 'graph',
    targetId?: string,
  ) =>
    apiFetch(`/api/projects/${projectId}/shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, target_id: targetId }),
    }).then(j<{ share_id: string; url: string }>),

  listShares: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/shares`).then(
      j<
        Array<{
          share_id: string;
          kind: string;
          title: string;
          created_at: string;
          revoked: boolean;
        }>
      >,
    ),

  revokeShare: (projectId: string, shareId: string) =>
    apiFetch(`/api/projects/${projectId}/shares/${shareId}/revoke`, {
      method: 'POST',
    }).then(j<{ revoked: boolean }>),

  /**
   * doc/12 §3.1.3: the user's own invite codes (Settings invite tab).
   * Lazily mints the user-grant batch server-side when the user first
   * qualifies, so calling this IS the grant trigger.
   */
  myInvites: async (): Promise<{
    gate: boolean;
    granted_now: boolean;
    codes: { code: string; used: boolean; used_at: string | null; revoked: boolean }[];
  }> => {
    const res = await apiFetch('/api/me/invites');
    if (!res.ok) throw new Error(`myInvites ${res.status}`);
    return res.json();
  },

  /** Persist the user's active project. Pass null to clear. Fire-and-forget. */
  setLastProject: (projectId: string | null) =>
    apiFetch('/api/me/last-project', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId }),
    }).then(j<{ ok: boolean }>),

  /** 计费重构（2026-06-28）：Stripe 一次性充值。后端按金额创建 Checkout
   *  Session（mode=payment），返回 hosted URL，前端 `window.location = url`
   *  跳转。`amountCents` 需在 $5–$500。未配置 Stripe 时后端 500 +
   *  "billing not configured"，调用方应提示"充值暂未开放"而非抛通用错误。 */
  createTopupSession: (amountCents: number) =>
    apiFetch('/api/billing/topup-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount_cents: amountCents }),
    }).then(j<{ url: string }>),

  /** 计费重构：当前钱包余额。Read-only；可轮询，调用方吞错。 */
  getWallet: () => apiFetch('/api/wallet').then(j<WalletStatus>),

  /** 计费重构：钱包交易历史（最新在前，上限 100）。 */
  getWalletTransactions: () =>
    apiFetch('/api/wallet/transactions').then(j<WalletTransaction[]>),

  /** PR-15 (i18n): persist the user-wide locale preference. Pass null to
   *  clear (fall back to browser detection). */
  setUserLocale: (locale: Locale | null) =>
    apiFetch('/api/me/locale', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale }),
    }).then(j<{ ok: boolean }>),

  /** PR-15 (i18n): read a thread's per-thread locale override. */
  getThreadLocale: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}/locale`).then(
      j<{ locale: Locale | null }>,
    ),

  /** PR-15 (i18n): set a thread's locale override. Pass null to clear
   *  (the thread then inherits the user / browser locale). */
  setThreadLocale: (threadId: string, locale: Locale | null) =>
    apiFetch(`/api/threads/${threadId}/locale`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale }),
    }).then(j<{ ok: boolean }>),

  /**
   * Persist the user's active thread within a project. Server validates
   * thread belongs to project + tenant; otherwise 400/404.
   */
  setLastThread: (projectId: string, threadId: string | null) =>
    apiFetch(`/api/projects/${projectId}/last-thread`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_id: threadId }),
    }).then(j<{ ok: boolean }>),

  /**
   * Card list for the lens. By default `include_archived=false` so the
   * default Time / Status view doesn't drown in junk cards. The lens
   * picker's "Show archived" toggle re-fetches with `true`.
   */
  listCards: (projectId: string, opts?: { includeArchived?: boolean }) => {
    const qs = opts?.includeArchived ? '?include_archived=true' : '';
    return apiFetch(`/api/projects/${projectId}/cards${qs}`).then(j<Card[]>);
  },

  /**
   * Lens view feed: every edge in the project, returned as a flat array.
   * The frontend computes per-card connection counts (incoming, outgoing,
   * contradicts) and joins them against the cards list. One small payload —
   * edges per project number in the hundreds.
   */
  listEdges: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/edges`).then(j<Edge[]>),

  /** Soft-hide / un-hide a card from the lens. Distinct from delete. */
  setCardArchived: (projectId: string, cardId: string, archived: boolean) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}/archived`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived }),
    }).then(j<Card>),

  patchCard: (projectId: string, cardId: string, patch: CardPatch) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(j<Card>),

  /** PR-41: set or clear `implementation_status` from the web UI. Pass
   *  `null` to write NULL (clear tracking). Server validates the value
   *  against the same allowlist the CLI uses — no client-side gate. */
  setCardImplementationStatus: (
    projectId: string,
    cardId: string,
    status: ImplementationStatus | null,
  ) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}/implementation-status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }).then(j<{ id: string; implementation_status: ImplementationStatus | null }>),

  /** Delete a card. Optional `reason` is persisted as `delete_reason` on
   *  the card_history row so the Deleted view can show why it was
   *  removed (rather than just "deleted by you"). */
  deleteCard: (projectId: string, cardId: string, reason?: string) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}`, {
      method: 'DELETE',
      headers: reason ? { 'Content-Type': 'application/json' } : undefined,
      body: reason ? JSON.stringify({ reason }) : undefined,
    }).then(j<DeleteCardOutput>),

  /** Full revision list for a card, newest first. Works on already-deleted
   *  cards too — used by the broken-wikilink tooltip / modal to show the
   *  history of a card that no longer exists. */
  listCardHistory: (projectId: string, cardId: string) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}/history`)
      .then(j<CardHistoryRevision[]>),

  /** Restore a card to a prior revision. Server pulls that revision's
   *  body/title/kind/pack and re-applies through the update_card pipeline,
   *  which writes a new op=patch history row (so the restore itself is
   *  recorded). The current state is overwritten in place. */
  restoreCardRevision: (projectId: string, cardId: string, revision: number) =>
    apiFetch(
      `/api/projects/${projectId}/cards/${cardId}/history/${revision}/restore`,
      { method: 'POST' },
    ).then(j<Card>),

  /** Project-level list of deleted cards, newest deletion first. Each
   *  entry is the op='delete' row carrying the snapshot of the card at
   *  the moment of deletion + delete_reason ("rejected: …", "resolved by
   *  c-X", "fs delete"). */
  listDeletedCards: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/deleted_cards`)
      .then(j<CardHistoryRevision[]>),

  /**
   * Promote a `gap_kind=interpretation_needed` card into a real card of the
   * proposed kind. Server reads `extra.proposed_kind` if `to_kind` is
   * omitted; preserves the card id; stamps `extra.promoted_at`.
   *
   * Pass `title` / `body` to apply edits in the same call ("Edit & Approve").
   * For plain Approve, send an empty body — server falls back to the
   * proposal's stored title/body.
   */
  promoteCard: (
    projectId: string,
    cardId: string,
    input: { to_kind?: string; title?: string; body?: string },
  ) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}/promote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(j<Card>),

  /**
   * Reject a gap proposal. Deletes the gap and appends a `dismissed_gaps`
   * entry to each card in its `extra.blocks` so the agent has a record next
   * time it considers a similar concern. Optional `reason` is stored verbatim.
   */
  rejectGap: (
    projectId: string,
    cardId: string,
    input: { reason?: string } = {},
  ) =>
    apiFetch(`/api/projects/${projectId}/cards/${cardId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(j<{ card_id: string; blocks_marked: string[] }>),

  /**
   * Stream a long-form artifact (design doc / pitch / etc.) generated from
   * the project's cards. Server emits SSE frames described by `GenerateEvent`:
   * `started` → many `delta` → `done` (or `error`). Promise resolves on
   * `done` with the final markdown + metadata, rejects on `error` / network
   * close. `onEvent` is called for every frame — use it to render markdown
   * progressively while the LLM is still streaming.
   *
   * EventSource doesn't support POST, so we use fetch + manual frame parsing.
   * Each SSE frame is `data: {json}\n\n`.
   */
  generate: async (
    projectId: string,
    template: string,
    onEvent: (e: GenerateEvent) => void,
  ): Promise<GenerateOutput> => {
    const res = await apiFetch(`/api/projects/${projectId}/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ template }),
    });
    if (!res.ok || !res.body) {
      const detail = await res.text().catch(() => '');
      throw new Error(`generate ${res.status}: ${detail}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let result: GenerateOutput | null = null;
    let errMsg: string | null = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const dataLine = frame
          .split('\n')
          .find((line) => line.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (!payload) continue;
        try {
          const evt = JSON.parse(payload) as GenerateEvent;
          onEvent(evt);
          if (evt.type === 'done') {
            result = {
              markdown: evt.markdown,
              template: evt.template,
              card_count: evt.card_count,
            };
          } else if (evt.type === 'error') {
            errMsg = evt.message;
          }
        } catch (err) {
          console.warn('bad SSE frame', payload, err);
        }
      }
    }
    if (errMsg) throw new Error(errMsg);
    if (!result) throw new Error('stream closed before done');
    return result;
  },

  // ── Documents ───────────────────────────────────────────────────────
  listDocuments: async (projectId: string): Promise<DocumentRecord[]> => {
    const res = await apiFetch(`/api/projects/${projectId}/documents`);
    if (!res.ok) throw new Error(`listDocuments ${res.status}`);
    return res.json();
  },

  getDocument: async (projectId: string, documentId: string): Promise<DocumentRecord> => {
    const res = await apiFetch(`/api/projects/${projectId}/documents/${documentId}`);
    if (!res.ok) throw new Error(`getDocument ${res.status}`);
    return res.json();
  },

  saveDocument: async (
    projectId: string,
    input: {
      title: string;
      template: string;
      markdown: string;
      source_snapshot: Array<{ card_id: string; mtime: string }>;
    },
  ): Promise<DocumentRecord> => {
    const res = await apiFetch(`/api/projects/${projectId}/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`saveDocument ${res.status}: ${detail}`);
    }
    return res.json();
  },

  deleteDocument: async (projectId: string, documentId: string): Promise<void> => {
    const res = await apiFetch(`/api/projects/${projectId}/documents/${documentId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`deleteDocument ${res.status}`);
  },

  /** Run the distill flow on a thread. Streams SSE events; resolves when the
   *  server emits `done` with the proposed cards. The user reviews these in
   *  the modal — nothing is persisted until they explicitly accept.
   *
   *  `mode` selects which prompt drives the extraction. Defaults to `rescue`
   *  (the original behavior) when omitted.
   *
   *  `targetMessageIndex` (optional) narrows the transcript to that single
   *  assistant message + its preceding user turn. Used by the inline
   *  "Convert to gaps" button on chat bubbles. */
  distillThread: async (
    threadId: string,
    onEvent: (e: DistillEvent) => void,
    mode: DistillMode = 'rescue',
    targetMessageIndex?: number,
  ): Promise<DistillOutput> => {
    const params = new URLSearchParams();
    if (mode !== 'rescue') params.set('mode', mode);
    if (targetMessageIndex !== undefined) {
      params.set('target_message_index', String(targetMessageIndex));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await apiFetch(`/api/threads/${threadId}/distill${qs}`, {
      method: 'POST',
      headers: { Accept: 'text/event-stream' },
    });
    if (!res.ok || !res.body) {
      const detail = await res.text().catch(() => '');
      throw new Error(`distill ${res.status}: ${detail}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let result: DistillOutput | null = null;
    let errMsg: string | null = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (!payload) continue;
        try {
          const evt = JSON.parse(payload) as DistillEvent;
          onEvent(evt);
          if (evt.type === 'done') result = { cards: evt.cards, gaps: evt.gaps };
          if (evt.type === 'error') errMsg = evt.message;
        } catch (err) {
          console.warn('bad distill SSE frame', payload, err);
        }
      }
    }
    if (errMsg) throw new Error(errMsg);
    if (result === null) throw new Error('distill stream closed before done');
    return result;
  },

  listFrames: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/frames`).then(j<FrameDef[]>),

  /**
   * Fetch a frame view. May trigger a multi-second LLM call on first hit
   * after a card change; cached after that.
   */
  getFrame: (projectId: string, frameId: string) =>
    apiFetch(`/api/projects/${projectId}/frames/${frameId}`).then(j<FrameView>),

  /** User drag = explicit override. Persisted; survives recomputation. */
  overridePlacement: (
    projectId: string,
    frameId: string,
    cardId: string,
    nodePath: string,
  ) =>
    apiFetch(
      `/api/projects/${projectId}/frames/${frameId}/placements/${cardId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_path: nodePath }),
      },
    ).then(j<unknown>),

  /** Clear a manual override → drop the placement row and mark the frame
   *  dirty so the next view recomputes via LLM/rule. Used by the pin click
   *  on overridden CardRows ("reset to LLM placement"). */
  clearPlacement: (projectId: string, frameId: string, cardId: string) =>
    apiFetch(
      `/api/projects/${projectId}/frames/${frameId}/placements/${cardId}`,
      { method: 'DELETE' },
    ).then(j<unknown>),

  /** Hardcoded library of frame templates. */
  listFrameTemplates: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/frame-templates`).then(j<FrameDef[]>),

  /** Clone a template into this project. */
  cloneFrameTemplate: (projectId: string, templateId: string, name?: string) =>
    apiFetch(`/api/projects/${projectId}/frames`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_template: templateId, ...(name ? { name } : {}) }),
    }).then(j<unknown>),

  /** Submit a custom topology (typically from an accepted suggest_frame). */
  createCustomFrame: (
    projectId: string,
    payload: { name: string; description?: string; topology: FrameNode[]; origin?: 'agent' | 'custom' },
  ) =>
    apiFetch(`/api/projects/${projectId}/frames`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, origin: payload.origin ?? 'custom' }),
    }).then(j<unknown>),

  /**
   * Stream the LLM frame-suggester. Server emits SSE frames:
   *   - `started` once with the card count (UI can render "reading N cards")
   *   - `tick` repeatedly as the model emits tool-call JSON (drives progress)
   *   - `done` once with the proposed FrameDef
   *   - `error` once if the suggester fails
   * Promise resolves on `done` and rejects on `error` / network close.
   */
  suggestFrame: async (
    projectId: string,
    minCards = 6,
    onEvent?: (e: SuggestEvent) => void,
  ): Promise<{ proposal: FrameDef; card_count: number }> => {
    const res = await apiFetch(`/api/projects/${projectId}/frames/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ min_cards: minCards }),
    });
    if (!res.ok || !res.body) {
      const detail = await res.text().catch(() => '');
      throw new Error(`suggest ${res.status}: ${detail}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let result: { proposal: FrameDef; card_count: number } | null = null;
    let errMsg: string | null = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const dataLine = frame
          .split('\n')
          .find((line) => line.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (!payload) continue;
        try {
          const evt = JSON.parse(payload) as SuggestEvent;
          onEvent?.(evt);
          if (evt.type === 'done') {
            result = { proposal: evt.proposal, card_count: evt.card_count };
          } else if (evt.type === 'error') {
            errMsg = evt.message;
          }
        } catch (err) {
          console.warn('bad SSE frame', payload, err);
        }
      }
    }
    if (errMsg) throw new Error(errMsg);
    if (!result) throw new Error('stream closed before done');
    return result;
  },

  /** Patch a frame's display metadata (name / description / priority). */
  patchFrame: (
    projectId: string,
    frameId: string,
    patch: { name?: string; description?: string; priority?: number },
  ) =>
    apiFetch(`/api/projects/${projectId}/frames/${frameId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(j<unknown>),

  deleteFrame: (projectId: string, frameId: string) =>
    apiFetch(`/api/projects/${projectId}/frames/${frameId}`, { method: 'DELETE' }).then(j<unknown>),

  recomputeFrame: (projectId: string, frameId: string) =>
    apiFetch(`/api/projects/${projectId}/frames/${frameId}/recompute`, {
      method: 'POST',
    }).then(j<unknown>),

  listThreads: (projectId: string) =>
    apiFetch(`/api/projects/${projectId}/threads`).then(j<Thread[]>),

  createThread: (projectId: string, title: string | null = null) =>
    apiFetch(`/api/projects/${projectId}/threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }).then(j<Thread>),

  archiveThread: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}`, { method: 'DELETE' }).then(j<{ thread_id: string; archived: boolean }>),

  /** Bump the user's read cursor for a thread to now() so the unread
   *  dot in the sidebar clears. Best-effort — the FE does an optimistic
   *  local clear before this resolves; failures are silent (the cursor
   *  will catch up on the next list_threads refetch). */
  markThreadRead: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}/read`, { method: 'POST' })
      .then(j<{ thread_id: string; last_read_at: string }>),

  /** doc/08 B-2: hand the device's APNs / FCM token to the server so it
   *  can push turn-complete / gap notifications to it. Best-effort — a
   *  failed registration just means this device receives no pushes. */
  registerPushToken: (token: string, platform: 'ios' | 'android') =>
    apiFetch('/api/push/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform }),
    }).then(j<{ ok: boolean }>),

  renameThread: (threadId: string, title: string) =>
    apiFetch(`/api/threads/${threadId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }).then(j<Thread>),

  /** PR-56: write a per-thread model override. Pass null (or the
   *  empty string) to clear it and revert to the server default. */
  setThreadModel: (threadId: string, model: string | null) =>
    apiFetch(`/api/threads/${threadId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: model ?? '' }),
    }).then(j<Thread>),

  /** PR-57: write the full per-thread model preference in one call.
   *  Each field is independently optional: omit to leave the column
   *  unchanged, set to `null` (or empty string for model/effort) to
   *  clear it. Used by the new TierPicker which exposes Models /
   *  Effort / Fast mode as separate controls. */
  setThreadModelPreference: (
    threadId: string,
    patch: { model?: string | null; effort?: string | null; fast_mode?: boolean },
  ) => {
    const body: Record<string, unknown> = {};
    if (patch.model !== undefined) body.model = patch.model ?? '';
    if (patch.effort !== undefined) body.effort = patch.effort ?? '';
    if (patch.fast_mode !== undefined) body.fast_mode = patch.fast_mode;
    return apiFetch(`/api/threads/${threadId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(j<Thread>);
  },

  /** PR-56: model picker choices. Auth-only; cache on the client
   *  (one fetch per session is enough — env-driven, doesn't change
   *  without a server restart). */
  getModelChoices: () => apiFetch('/api/models').then(j<ModelChoices>),

  listMessages: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}/messages`).then(j<SessionMessage[]>),

  /** PR-55: token-usage snapshot for the chat header's context bar.
   *  Cheap (parses the persisted session blob and sums per-message
   *  `usage`); fetched once on thread open, then refreshed by the
   *  SSE `turn_usage` event after each turn. */
  getThreadUsage: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}/usage`).then(j<ThreadUsage>),

  /**
   * Start a chat turn. POST sends the user text; the server claims a
   * `turn_id` and returns immediately (no streaming on this request).
   * Use `attachTurn` to subscribe to the turn's event stream — the
   * stream is shared across devices and survives client reconnects.
   *
   * `attachments` carries IMAGE ref filenames only — the server presigns
   * them and injects them as `image_url` content blocks for vision-capable
   * models. Non-image refs (.md/.txt/.pdf/etc.) should stay in the text
   * body via an `attached: ...` prelude so the agent can pull them with
   * `read_ref` when relevant.
   *
   * 409 Conflict: another device already started a turn on this thread.
   * The error includes the `active_turn_id` so the caller can switch
   * to attach mode against the running stream.
   */
  startTurn: async (
    threadId: string,
    text: string,
    attachments: string[] = [],
  ): Promise<StartTurnResponse> => {
    const body =
      attachments.length > 0 ? { text, attachments } : { text };
    const res = await apiFetch(`/api/threads/${threadId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      const conflict = (await res.json().catch(() => ({}))) as {
        active_turn_id?: string;
        thread_id?: string;
      };
      const err = new Error(
        `turn already in flight on this thread (active_turn_id=${conflict.active_turn_id})`,
      ) as Error & { conflictTurnId?: string };
      err.conflictTurnId = conflict.active_turn_id;
      throw err;
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`startTurn ${res.status}: ${detail}`);
    }
    const resp = (await res.json()) as StartTurnResponse;
    // Tag this turn as "mine" so the project SSE handler skips the
    // refetch (which would erase locally-streamed thinking bubbles —
    // the server doesn't persist reasoning).
    myStartedTurnIds.add(resp.turn_id);
    setTimeout(
      () => myStartedTurnIds.delete(resp.turn_id),
      MY_TURN_TTL_MS,
    );
    return resp;
  },

  /**
   * Subscribe to a turn's event stream. Uses a native `EventSource` so
   * the browser auto-reconnects (with `Last-Event-ID`) on transient
   * network drops — no manual retry logic needed for that path.
   *
   * `fromSeq` is for explicit re-attach scenarios (e.g. mobile resume
   * from background): pass the highest seq the caller has already seen
   * and the server replays anything newer plus the live tail. Omit on
   * fresh attach to replay everything still in the hub's buffer.
   *
   * Returns an unsubscribe function. Stream auto-closes after a
   * terminal `turn_done` or `error` event; `onDone` fires once when
   * that happens (or when the caller invokes unsubscribe).
   */
  attachTurn: (
    threadId: string,
    turnId: string,
    onEvent: (e: TurnEvent) => void,
    opts?: {
      fromSeq?: number;
      onDone?: () => void;
      onError?: (err: unknown) => void;
    },
  ): (() => void) => {
    const url =
      opts?.fromSeq !== undefined
        ? `/api/threads/${threadId}/turns/${turnId}/stream?from_seq=${opts.fromSeq}`
        : `/api/threads/${threadId}/turns/${turnId}/stream`;
    const src = new EventSource(url);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      src.close();
      opts?.onDone?.();
    };
    src.onmessage = (msg) => {
      try {
        const ev = JSON.parse(msg.data) as TurnEvent;
        onEvent(ev);
        if (ev.type === 'turn_done' || ev.type === 'error') {
          finish();
        }
      } catch (err) {
        console.warn('bad turn SSE frame', msg.data, err);
      }
    };
    src.onerror = (e) => {
      // If we've already received `turn_done` (or the caller invoked the
      // returned unsubscribe), the EventSource is being torn down on our
      // terms. iOS Safari sometimes fires one trailing `onerror` with
      // readyState=CLOSED *after* our own src.close() — surfacing that as
      // a "send failed" is wrong; the turn already succeeded.
      if (done) return;
      // EventSource auto-retries on transient errors (network blip,
      // server restart). It only stays in CLOSED state if the server
      // returned 4xx/5xx, which means the turn is gone — surface to
      // the caller so they can fall back to a session refetch.
      if (src.readyState === EventSource.CLOSED) {
        // Event objects stringify to "[object Event]", which is useless
        // in a toast. Wrap as a real Error with a meaningful message so
        // downstream `(e as Error).message` reads cleanly.
        opts?.onError?.(new Error('stream closed unexpectedly'));
        finish();
      } else {
        console.warn('turn SSE transient error, will retry', e);
      }
    };
    return finish;
  },

  /**
   * Look up the active (or just-finished but still in grace) turn on
   * this thread. Used on cold attach — page load, app resume, or
   * cross-device thread open — to decide whether to subscribe.
   */
  getActiveTurn: (threadId: string) =>
    apiFetch(`/api/threads/${threadId}/active_turn`).then(j<ActiveTurn | null>),

  /**
   * Compatibility shim: drive a turn end-to-end with the old "POST
   * waits for the stream" semantics existing call sites still use.
   * Internally splits into `startTurn` + `attachTurn` so all the new
   * protocol benefits (resume, multi-device) come for free.
   *
   * `onTurnId` fires once as soon as the server hands back a turn_id
   * (before any SSE event arrives). Lets the caller park the id for
   * cancellation / resume without having to switch to the explicit
   * `startTurn` + `attachTurn` pair.
   */
  streamMessage: async (
    threadId: string,
    text: string,
    onEvent: (e: TurnEvent) => void,
    attachments: string[] = [],
    onTurnId?: (id: string) => void,
  ): Promise<void> => {
    const { turn_id } = await api.startTurn(threadId, text, attachments);
    onTurnId?.(turn_id);
    await new Promise<void>((resolve, reject) => {
      api.attachTurn(threadId, turn_id, onEvent, {
        onDone: () => resolve(),
        onError: (e) => reject(e instanceof Error ? e : new Error(String(e))),
      });
    });
  },

  /**
   * Trip the cancel token on an in-flight turn. The server drops the
   * LLM SSE stream and aborts any running pysbx call, then emits a
   * single `TurnEvent::Error { message: "stopped by user" }` followed
   * by the EventSource closing. Idempotent: cancelling a turn that
   * already finished returns 204 with no effect.
   */
  cancelTurn: async (threadId: string, turnId: string): Promise<void> => {
    const res = await apiFetch(
      `/api/threads/${threadId}/turns/${turnId}/cancel`,
      { method: 'POST' },
    );
    if (!res.ok && res.status !== 204) {
      const detail = await res.text().catch(() => '');
      throw new Error(`cancelTurn ${res.status}: ${detail}`);
    }
  },

  /**
   * Subscribe to project-scoped state changes (CardWritten, ...).
   *
   * `onReopen` fires every time the underlying EventSource re-establishes
   * its connection. The browser auto-reconnects after a network blip,
   * server restart, or laptop wake — but the SSE protocol does NOT replay
   * events missed during the gap. So the reconnect is the right moment
   * for the caller to do a "refetch from REST" sweep and pick up any
   * mutations that landed while we were offline.
   *
   * The first connection-open after subscribe also fires `onReopen` —
   * harmless, and lets the caller treat all opens uniformly. Both the
   * initial fetch and the recover-after-gap fetch run the same code
   * path, which is what we want.
   */
  events: (
    projectId: string,
    onEvent: (e: WireEvent) => void,
    onReopen?: () => void,
  ) => {
    // EventSource's native auto-reconnect is a fixed ~3s with no give-up
    // condition — happy to hammer a permanently-failing endpoint (e.g.
    // expired session → 401) every 3s for the lifetime of the tab. After
    // an overnight sleep that compounds into a visible CPU floor in
    // "Google Chrome Helper" with the tab merely idle. We close on every
    // onerror (suppressing the native retry) and reconnect ourselves with
    // exponential backoff capped at 60s. The CoreProvider cleanup that
    // fires on auth-out closes the subscription entirely, so a session
    // expiry can't keep the loop alive.
    let closed = false;
    let src: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 1000;
    const RETRY_MAX = 60_000;

    const connect = () => {
      if (closed) return;
      src = new EventSource(`/api/projects/${projectId}/events`);
      src.onopen = () => {
        retryDelay = 1000;
        onReopen?.();
      };
      src.onmessage = (msg) => {
        try {
          onEvent(JSON.parse(msg.data) as WireEvent);
        } catch (err) {
          console.warn('bad SSE payload', msg.data, err);
        }
      };
      src.onerror = (e) => {
        if (closed) return;
        console.warn('SSE error, retrying in', retryDelay, 'ms', e);
        src?.close();
        src = null;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, RETRY_MAX);
      };
    };
    connect();
    return () => {
      closed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      src?.close();
      src = null;
    };
  },

  // ── Admin · errors (PR-22) ───────────────────────────────────────
  //
  // Self-built error monitoring (Sentry replacement). Fed by
  // /api/errors/report (frontend onerror / unhandledrejection) +
  // the backend tracing CaptureLayer. Admin-gated server-side; the
  // FE link is also admin-gated via me.email === 'dev@local'.

  listAdminErrors: (params?: {
    includeResolved?: boolean;
    source?: 'frontend' | 'backend';
    /** PR-24: filter by level. Defaults to undefined (all levels);
     *  the AdminErrors UI passes 'error' on first load to hide the
     *  WARN-level slow-query / GLM-quirk noise stream. */
    level?: 'error' | 'warn';
    limit?: number;
    offset?: number;
  }) => {
    const usp = new URLSearchParams();
    if (params?.includeResolved) usp.set('include_resolved', 'true');
    if (params?.source) usp.set('source', params.source);
    if (params?.level) usp.set('level', params.level);
    if (params?.limit != null) usp.set('limit', String(params.limit));
    if (params?.offset != null) usp.set('offset', String(params.offset));
    const qs = usp.toString();
    return apiFetch(`/api/admin/errors${qs ? `?${qs}` : ''}`).then(j<{
      errors: ErrorRow[];
      unresolved_count: number;
    }>);
  },

  resolveAdminError: async (id: string): Promise<void> => {
    const res = await apiFetch(`/api/admin/errors/${id}/resolve`, {
      method: 'POST',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`resolveAdminError ${res.status}: ${text}`);
    }
  },

  // ── Admin: Python sandbox monitor (doc/09 Phase 1) ───────────────
  //
  // Backed by /api/admin/sandbox — recent python_compute executions plus
  // a 24h failure / limiter-rejection breakdown. Same require_admin gate.

  listAdminSandbox: () =>
    apiFetch('/api/admin/sandbox').then(j<{
      recent: SandboxExecRow[];
      failures_24h: { error: string; count: number }[];
      window_hours: number;
    }>),

  // ── Admin: billing dashboard（计费重构：钱包视角）────────────────
  //
  // 全站总余额 / 累计充值 / 实收消费 / 真实成本 / 毛利 + 每用户余额表。
  // 单次聚合查询驱动整个 Admin → Billing tab。
  adminBilling: () =>
    apiFetch('/api/admin/billing').then(j<{
      total_balance_cents: number;
      total_topup_cents: number;
      total_spend_cents: number;
      total_bonus_cents: number;
      wallet_count: number;
      real_cost_cents: number;
      gross_profit_cents: number;
      users: Array<{
        user_id: string;
        email: string | null;
        display_name: string | null;
        balance_cents: number;
        topup_cents: number;
        spend_cents: number;
        updated_at: string;
      }>;
    }>),

  // ── Admin: invite ledger (doc/12 §3.1.4) ─────────────────────────

  adminListInvites: () =>
    apiFetch('/api/admin/invites').then(j<{
      invites: Array<{
        code: string;
        batch_label: string;
        created_at: string;
        used_at: string | null;
        revoked: boolean;
        creator_email: string | null;
        redeemer_email: string | null;
      }>;
    }>),

  adminGenerateInvites: (count: number, batchLabel: string) =>
    apiFetch('/api/admin/invites/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count, batch_label: batchLabel }),
    }).then(j<{ created: number; codes: string[] }>),

  adminRevokeInvite: (code: string) =>
    apiFetch(`/api/admin/invites/${encodeURIComponent(code)}/revoke`, {
      method: 'POST',
    }).then(j<{ revoked: boolean }>),

  adminListWaitlist: () =>
    apiFetch('/api/admin/waitlist').then(j<{
      waitlist: Array<{
        email: string;
        locale: string | null;
        created_at: string;
        invited_at: string | null;
      }>;
    }>),

  adminMarkWaitlistInvited: (email: string) =>
    apiFetch(`/api/admin/waitlist/${encodeURIComponent(email)}/mark-invited`, {
      method: 'POST',
    }).then(j<{ marked: boolean }>),

  // ── Admin: LLM usage panel ───────────────────────────────────────
  //
  // Backed by /api/admin/usage (list + aggregate) and
  // /api/admin/usage/:id/payload (drill-in). Same require_admin gate
  // server-side. Window defaults to last 7 days when since/until omitted.

  listAdminUsage: (params?: {
    since?: string; // ISO 8601
    until?: string;
    userId?: string;
    tenantId?: string;
    task?: string;
    tierId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const usp = new URLSearchParams();
    if (params?.since) usp.set('since', params.since);
    if (params?.until) usp.set('until', params.until);
    if (params?.userId) usp.set('user_id', params.userId);
    if (params?.tenantId) usp.set('tenant_id', params.tenantId);
    if (params?.task) usp.set('task', params.task);
    if (params?.tierId) usp.set('tier_id', params.tierId);
    if (params?.status) usp.set('status', params.status);
    if (params?.limit != null) usp.set('limit', String(params.limit));
    if (params?.offset != null) usp.set('offset', String(params.offset));
    const qs = usp.toString();
    return apiFetch(`/api/admin/usage${qs ? `?${qs}` : ''}`).then(j<{
      events: UsageEventRow[];
      aggregate: UsageAggregate;
      filter: { since: string; until: string };
    }>);
  },

  getAdminUsagePayload: (eventId: string) =>
    apiFetch(`/api/admin/usage/${eventId}/payload`).then(j<UsagePayloadRow>),

  getAdminOverview: () =>
    apiFetch('/api/admin/overview').then(j<AdminOverview>),

  listAdminUsers: () =>
    apiFetch('/api/admin/users').then(j<{ users: AdminUserRow[] }>),

  // ── API tokens (PR-04 / PR-39) ───────────────────────────────────
  //
  // Long-lived bearer tokens that authenticate the `draftlize` CLI and
  // any external MCP client. Settings → API Tokens drives this. The
  // raw secret is only surfaced at creation time — the UI shows it
  // once with a copy button and a "save this now" warning.

  listApiTokens: () => apiFetch('/api/tokens').then(j<ApiToken[]>),

  createApiToken: (name: string, scopes?: string[]) =>
    apiFetch('/api/tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scopes }),
    }).then(j<CreateApiTokenResponse>),

  revokeApiToken: async (id: string): Promise<void> => {
    const res = await apiFetch(`/api/tokens/${id}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 204) {
      const text = await res.text().catch(() => '');
      throw new Error(`revokeApiToken ${res.status}: ${text}`);
    }
  },
};

/** Client-side memory of the user's last-picked tier so that new threads
 *  inherit it instead of falling back to the server's `DRAFTLIZE_MODEL`
 *  default. The TierPicker writes here whenever the user changes the
 *  model; `createThreadWithPreference` reads it after each thread create
 *  and applies it via setThreadModelPreference. localStorage-only — no
 *  cross-device sync. Effort/fast_mode are intentionally NOT persisted:
 *  they're tier-specific (effort levels differ per tier, fast mode only
 *  matters when thinking is on) and per-conversation choices. */
const PREFERRED_TIER_STORAGE_KEY = 'draftlize.preferredTier';

export function getPreferredTier(): string | null {
  try {
    const v = localStorage.getItem(PREFERRED_TIER_STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function setPreferredTier(tierId: string | null | undefined): void {
  try {
    if (tierId == null || tierId === '') {
      localStorage.removeItem(PREFERRED_TIER_STORAGE_KEY);
    } else {
      localStorage.setItem(PREFERRED_TIER_STORAGE_KEY, tierId);
    }
  } catch {
    // Storage disabled (private mode, quota); silently degrade to no
    // preference — the new thread will use the server default.
  }
}

/** Wraps api.createThread and applies the locally remembered tier (if
 *  any) to the new thread before returning it. Failure to apply the
 *  preference is non-fatal: we return the thread as-created so the caller
 *  can proceed; the server default just becomes the effective model. */
export async function createThreadWithPreference(
  projectId: string,
  title: string | null = null,
): Promise<Thread> {
  const t = await api.createThread(projectId, title);
  const tier = getPreferredTier();
  if (!tier) return t;
  try {
    return await api.setThreadModelPreference(t.id, { model: tier });
  } catch {
    return t;
  }
}

/** PR-22: shape returned by /api/admin/errors. Mirrors `ErrorRow` in
 *  draftlize-storage. `context` is a JSONB grab-bag (user_agent,
 *  request_id, etc.) — render as preformatted text in the admin UI. */
/** doc/09 Phase 1: one row of /api/admin/sandbox `recent`. Mirrors
 *  `SandboxExecRow` in draftlize-storage. `error` is the structured
 *  failure code (null on a clean run); `machine_id` is null when no Fly
 *  machine was created (a limiter rejection). */
export interface SandboxExecRow {
  id: number;
  tenant_id: string;
  project_id: string | null;
  thread_id: string | null;
  card_id: string | null;
  code: string;
  stdout: string | null;
  stderr: string | null;
  exit_code: number | null;
  error: string | null;
  duration_ms: number | null;
  machine_id: string | null;
  created_at: string;
}

export interface ErrorRow {
  id: string;
  occurred_at: string;
  source: 'frontend' | 'backend' | string;
  level: string;
  message: string;
  stack_trace: string | null;
  location: string | null;
  user_id: string | null;
  tenant_id: string | null;
  context: Record<string, unknown>;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
}

/** Admin LLM-usage row. Mirrors `UsageEventRow` in draftlize-storage.
 *  Token columns are bigint-as-number — JS can hold these safely up to
 *  2^53 which is far above any realistic per-call count. cost_cents is
 *  NUMERIC(12,4) decoded as a number; treat as a display-only value. */
export interface UsageEventRow {
  id: string;
  tenant_id: string;
  user_id: string;
  task: string;
  model: string;
  provider: string;
  tier_id: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  reasoning_tokens: number;
  cost_cents: number;
  latency_ms: number | null;
  status: string;
  error_code: string | null;
  thread_id: string | null;
  created_at: string;
}

/** Roll-up over a UsageQuery filter set. Drives the AdminUsage top
 *  cards. avg_latency_ms is null when no row in the filter has a
 *  recorded latency (legacy data). */
export interface UsageAggregate {
  event_count: number;
  error_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  reasoning_tokens: number;
  cost_cents: number;
  avg_latency_ms: number | null;
}

/** Sibling payload row. Pruned on a 30-day rolling TTL — the list
 *  endpoint may return events whose payload 404s here. */
export interface UsagePayloadRow {
  event_id: string;
  request_messages: unknown;
  response_text: string;
  reasoning_text: string | null;
  tool_calls: unknown | null;
  raw_usage: unknown | null;
  created_at: string;
}

/** Admin overview response. Drives the Overview tab on the unified
 *  admin page — KPI cards (counts + 24h rollup) + a recent-activity
 *  slice the Usage row renderer can chew on. */
export interface AdminOverview {
  counts: {
    users: number;
    tenants: number;
    threads: number;
    cards: number;
  };
  today: {
    calls: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
    errors: number;
    avg_latency_ms: number | null;
  };
  errors_unresolved: number;
  recent_calls: UsageEventRow[];
}

/** Per-user row on the Users tab. Aggregates LLM activity + error
 *  count for one user; `last_activity` is the most recent usage_events
 *  row created_at, or null if the user has never made an LLM call. */
export interface AdminUserRow {
  id: string;
  email: string | null;
  github_login: string | null;
  tenant_id: string;
  tenant_name: string;
  created_at: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  errors: number;
  last_activity: string | null;
  is_admin: boolean;
}
